/**
 * machineColors.js
 * Centralised machine-colour utilities.
 *
 * Rules:
 *  1. A machine's colour is "user-assigned" when it is a non-empty hex string
 *     that is NOT black (#000000 / #000).
 *  2. Unassigned machines receive a distinct colour from DISTINCT_MACHINE_PALETTE
 *     that does not clash with any already-used colour (user-assigned or
 *     previously auto-assigned).  Golden-ratio HSL is used as an overflow
 *     strategy when the palette is exhausted.
 */

// 36-colour high-contrast palette – avoids near-blacks and near-whites.
export const DISTINCT_MACHINE_PALETTE = [
  "#e11d48", // Rose 600
  "#7c3aed", // Violet 600
  "#2563eb", // Blue 600
  "#0891b2", // Cyan 600
  "#059669", // Emerald 600
  "#d97706", // Amber 600
  "#dc2626", // Red 600
  "#9333ea", // Purple 600
  "#0284c7", // Sky 600
  "#0d9488", // Teal 600
  "#16a34a", // Green 600
  "#ca8a04", // Yellow 600
  "#db2777", // Pink 600
  "#4f46e5", // Indigo 600
  "#0369a1", // Sky 700
  "#047857", // Emerald 700
  "#b45309", // Amber 700
  "#be185d", // Pink 700
  "#6d28d9", // Violet 700
  "#1d4ed8", // Blue 700
  "#0e7490", // Cyan 700
  "#15803d", // Green 700
  "#b91c1c", // Red 700
  "#7e22ce", // Purple 700
  "#c2410c", // Orange 700
  "#0f766e", // Teal 700
  "#1e40af", // Blue 800
  "#065f46", // Emerald 800
  "#92400e", // Amber 800
  "#9d174d", // Pink 800
  "#5b21b6", // Violet 800
  "#075985", // Sky 800
  "#166534", // Green 800
  "#991b1b", // Red 800
  "#6b21a8", // Purple 800
  "#c2410c", // Orange 700 (cycle start for overflow)
];

/**
 * Normalise a colour value to lowercase; return null if it looks "unset"
 * (empty string, null, undefined, #000000, #000, black).
 */
export const normaliseColor = (color) => {
  if (!color) return null;
  const c = String(color).trim().toLowerCase();
  if (c === "" || c === "#000000" || c === "#000" || c === "black") return null;
  return c;
};

/**
 * Pick the first colour from DISTINCT_MACHINE_PALETTE that is not already
 * present in `usedColors` (a Set of lowercase hex strings).  Falls back to
 * a golden-ratio HSL colour when the palette is exhausted.
 *
 * @param {number} overflowIndex - monotonically increasing index used for the
 *   golden-ratio fallback (not the palette index).
 * @param {Set<string>} usedColors
 * @returns {string} hex / hsl colour string
 */
export const getDistinctDefaultColor = (overflowIndex, usedColors = new Set()) => {
  for (const c of DISTINCT_MACHINE_PALETTE) {
    if (!usedColors.has(c.toLowerCase())) return c;
  }
  // Golden-ratio overflow: evenly spaced hues, guaranteed not to collide
  // within a reasonable number of machines.
  const goldenAngle = 137.508;
  let hue = (overflowIndex * goldenAngle) % 360;
  // Skip hues that are visually too close to used palette entries (rough guard)
  let candidate = `hsl(${Math.round(hue)}, 70%, 42%)`;
  return candidate;
};

/**
 * Build a complete { machineName → colour } map for a list of machines.
 *
 * Pass 1 – collect all user-assigned explicit colours; mark as used.
 * Pass 2 – assign unique auto colours to every machine that still lacks one,
 *           ensuring no two unassigned machines share the same default colour.
 *
 * @param {Array<{machine_name: string, color?: string}>} machineList
 *   Array of machine objects from the backend (e.g. /client-machines).
 * @param {Object} [extraColorMap={}]
 *   Additional { machineName → color } pairs (e.g. from /machines_data Hierarchy)
 *   that override or supplement the list. User-assigned colours in extraColorMap
 *   are respected and counted as "used".
 * @returns {{ [machineName: string]: string }}
 */
export const buildMachineColorMap = (machineList = [], extraColorMap = {}) => {
  const result = {};
  const usedColors = new Set();

  // ── Pass 1: collect explicit user-assigned colours ─────────────────────────
  // From the extra map (Hierarchy / machines_data response)
  Object.entries(extraColorMap).forEach(([name, color]) => {
    const norm = normaliseColor(color);
    if (norm) {
      result[name] = norm;
      usedColors.add(norm);
    }
  });

  // From the machine list (client-machines)
  (machineList || []).forEach((m) => {
    const name = m.machine_name || m.name;
    if (!name) return;
    const norm = normaliseColor(m.color);
    if (norm) {
      result[name] = norm;
      usedColors.add(norm);
    }
  });

  // ── Pass 2: assign non-colliding default colours to all unassigned machines ─
  let overflowIdx = 0;
  (machineList || []).forEach((m) => {
    const name = m.machine_name || m.name;
    if (!name) return;
    if (result[name]) return; // already assigned

    const autoColor = getDistinctDefaultColor(overflowIdx++, usedColors);
    result[name] = autoColor;
    usedColors.add(autoColor.toLowerCase());
  });

  return result;
};

/**
 * Convenience lookup: returns the machine's colour from the map, or generates
 * a stable HSL fallback based on the machine name hash.
 *
 * @param {string} machineName
 * @param {{ [name: string]: string }} machineColorMap
 * @param {number} [fallbackIndex=0]
 * @returns {string}
 */
export const getMachineColor = (machineName, machineColorMap = {}, fallbackIndex = 0) => {
  if (machineColorMap[machineName]) return machineColorMap[machineName];
  // Deterministic hue from name hash so the colour is stable across renders
  let hash = 0;
  for (let i = 0; i < (machineName || "").length; i++) {
    hash = (hash * 31 + machineName.charCodeAt(i)) >>> 0;
  }
  const hue = hash % 360;
  return `hsl(${hue}, 65%, 42%)`;
};
